import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Loader2, LogOut, Mail, User as UserIcon, Shield } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [picture, setPicture] = useState(user?.picture || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const uploadPic = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/files/upload?folder=avatars", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPicture(res.data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/auth/profile", { name, picture });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl text-charcoal">Your Profile</h1>
        <p className="text-charcoal/60 mt-2">Manage how you appear inside CelebrAI.</p>
      </div>

      <form onSubmit={save} className="card-surface p-6 sm:p-8 space-y-6">
        <div className="flex items-center gap-4 sm:gap-5">
          <div className="relative flex-shrink-0">
            {picture ? (
              <img src={picture} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover ring-4 ring-white shadow-lg" />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-rose-300 to-violet-400 flex items-center justify-center font-bold text-3xl text-white ring-4 ring-white shadow-lg">
                {(name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="profile-upload-btn"
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full rose-glow text-white flex items-center justify-center border-2 border-white shadow-lg">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" strokeWidth={2} />}
            </button>
            <input ref={fileRef} type="file" hidden accept="image/*" onChange={(e) => uploadPic(e.target.files?.[0])} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-xl text-charcoal break-words">{user?.name}</p>
            <p className="text-sm text-charcoal/60 flex items-start gap-1.5 mt-1">
              <Mail className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
              <span className="break-all min-w-0" data-testid="profile-email">{user?.email}</span>
            </p>
            {user?.role === "admin" && (
              <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-100 px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-bold text-rose-600">
                <Shield className="w-3 h-3" strokeWidth={2} /> Admin
              </span>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">Display name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name"
            className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" />
        </div>

        <div className="flex flex-wrap gap-3 justify-between items-center pt-2 border-t border-warm">
          <button type="button" onClick={doLogout} data-testid="profile-logout"
            className="pill-btn pill-btn-secondary text-sm">
            <LogOut className="w-4 h-4" strokeWidth={1.75} /> Sign out
          </button>
          <button type="submit" disabled={busy} data-testid="profile-save"
            className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}>
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
